import { apiClient } from './client';
import type { PaymentProvider, PaymentReader } from './types';

type PaymentProviderType = 'stripe' | 'square';

export const paymentProvidersApi = {
  async list(): Promise<{ providers: PaymentProvider[] }> {
    return apiClient.request<{ providers: PaymentProvider[] }>('/payment/providers');
  },

  async get(provider: PaymentProviderType): Promise<{ provider: PaymentProvider }> {
    return apiClient.request<{ provider: PaymentProvider }>(`/payment/providers/${provider}`);
  },

  async configure(
    provider: PaymentProviderType,
    data: {
      enabled?: boolean;
      testMode?: boolean;
      credentials: {
        publishableKey?: string;
        secretKey?: string;
        locationId?: string;
        applicationId?: string;
        accessToken?: string;
      };
    }
  ): Promise<{ provider: PaymentProvider; message: string }> {
    return apiClient.request(`/payment/providers/${provider}`, {
      method: 'POST',
      body: data,
    });
  },

  async update(
    provider: PaymentProviderType,
    data: {
      enabled?: boolean;
      testMode?: boolean;
      credentials?: {
        publishableKey?: string;
        secretKey?: string;
        locationId?: string;
        applicationId?: string;
        accessToken?: string;
      };
    }
  ): Promise<{ provider: PaymentProvider }> {
    return apiClient.request(`/payment/providers/${provider}`, {
      method: 'PUT',
      body: data,
    });
  },

  async disable(provider: PaymentProviderType): Promise<{ message: string }> {
    return apiClient.request(`/payment/providers/${provider}`, { method: 'DELETE' });
  },
};

export const paymentReadersApi = {
  async list(params?: {
    provider?: PaymentProviderType;
    locationId?: number;
  }): Promise<{ readers: PaymentReader[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.provider) searchParams.set('provider', params.provider);
    if (params?.locationId) searchParams.set('locationId', String(params.locationId));

    const query = searchParams.toString();
    return apiClient.request<{ readers: PaymentReader[]; total: number }>(
      `/payment/readers${query ? `?${query}` : ''}`
    );
  },

  async register(data: {
    provider: PaymentProviderType;
    serialNumber: string;
    deviceType: string;
    label: string;
    locationId?: number;
  }): Promise<{ reader: PaymentReader }> {
    return apiClient.request('/payment/readers', {
      method: 'POST',
      body: data,
    });
  },

  async update(
    readerId: string,
    data: { label?: string; locationId?: number | null }
  ): Promise<{ reader: PaymentReader }> {
    return apiClient.request(`/payment/readers/${readerId}`, {
      method: 'PUT',
      body: data,
    });
  },

  async unregister(readerId: string): Promise<{ message: string }> {
    return apiClient.request(`/payment/readers/${readerId}`, { method: 'DELETE' });
  },
};
